import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Sun, Moon, ChevronRight, Trophy, Calendar, TrendingDown, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FoundersCapIcon } from '../components/icons/FoundersCapIcon';
import { THEMES, DEFAULT_LOGO_URL } from '../components/landing/landingConstants';
import { useNavigate } from 'react-router-dom';
import './PricingSliderMockup.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useTweenedNumber — small hook that animates a numeric `target` value over
 * `duration` ms with ease-out cubic. Returns the currently-animating value.
 * Used by the slider mockup to digit-roll Per Week, Total, and Savings
 * whenever the slider position OR currency selection changes.
 */
function useTweenedNumber(target, duration = 350) {
  const [value, setValue] = useState(target);
  useEffect(() => {
    let raf;
    const start = value;
    const end = target;
    const t0 = performance.now();
    const tick = (now) => {
      const elapsed = now - t0;
      const tFrac = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - tFrac, 3);
      setValue(start + (end - start) * eased);
      if (tFrac < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return value;
}

/**
 * MOCKUP / PREVIEW PAGE — "Sign Your Contract" slider pricing.
 *
 * Lets a coach drag a slider from 1 → 52 weeks. Per-week rate interpolates
 * linearly across three anchor points pulled live from `/api/pricing`:
 *   - week  1  → weekly base
 *   - week  4  → monthly base / 4.33  (per-week equivalent)
 *   - week 52  → annual base / 52     (per-week equivalent)
 *
 * Not wired into the main nav. Reach it at /pricing-slider-mockup for
 * design review. If approved, the math + UI can replace or sit alongside
 * the existing PricingSection.
 */
export default function PricingSliderMockup() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'dark');
  const isDark = theme === 'dark';
  const t = THEMES[theme];

  const [weeks, setWeeks] = useState(13); // start at 1 quarter — gentle midpoint
  const [pricing, setPricing] = useState(null);
  const [currencyRates, setCurrencyRates] = useState({});
  const [currency, setCurrency] = useState('eur');
  const [ballRotation, setBallRotation] = useState(0); // accumulated thumb rotation
  const [testimonialMode, setTestimonialMode] = useState(false); // Option A: tap the Founders' Cap tile to flip the main card into "Cap" state
  const prevWeeksRef = useRef(13);
  const mockupSliderRef = useRef(null);

  // Founders' Cap (lifetime tier) configuration. When promoted to production
  // this becomes CMS-editable: name, tagline, price per currency, founders
  // cap count, claimed count, scarcity copy, optional cap-icon variant.
  const TESTIMONIAL = useMemo(() => ({
    name: "Founders' Cap",
    tagline: "Be in from day one. Your Founders' Cap locks today's price forever \u2014 and earns you a new Cap every contract anniversary.",
    priceEUR: 59.99,
    founders: { total: 100, claimed: 23 },
  }), []);

  // The Tango ball image is the default thumb. Once promoted to production,
  // admins will be able to upload their own ball image via CMS — this URL stays
  // as the fallback / default.
  const ballFile = 'https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/jyfl98do_Untitled%20design%20-%202026-06-12T185108.908.png';

  // Currency config — mirrors PricingSection.js so the mockup reflects real-world UX.
  const currencyConfig = useMemo(() => ({
    eur: { symbol: '\u20AC', label: 'EUR', rate: 1 },
    gbp: { symbol: '\u00A3', label: 'GBP', rate: parseFloat(currencyRates.gbp) || 0.84 },
    usd: { symbol: '$',      label: 'USD', rate: parseFloat(currencyRates.usd) || 1.09 },
  }), [currencyRates]);
  const cc = currencyConfig[currency] || currencyConfig.eur;

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricing(r.data)).catch(() => {});
    // Pick up CMS-configured FX rates if set; otherwise the defaults in
    // currencyConfig stand in (0.84 GBP / 1.09 USD).
    axios.get(`${API}/landing/content`)
      .then(r => setCurrencyRates(r.data?.pricing?.currency_rates || {}))
      .catch(() => {});
  }, []);

  // Roll the ball in the direction of travel. Each week step rolls ~40deg,
  // which feels physical without being dizzying across the 1→52 range.
  useEffect(() => {
    const delta = weeks - prevWeeksRef.current;
    prevWeeksRef.current = weeks;
    if (delta !== 0) setBallRotation((r) => r + delta * 40);
  }, [weeks]);

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('landingTheme', next);
  };

  // Anchor per-week rates from live pricing config — converted to the active currency.
  const anchors = useMemo(() => {
    const w = (Number(pricing?.plans?.weekly?.base_amount) || 2.99) * cc.rate;
    const m = (Number(pricing?.plans?.monthly?.base_amount) || 8.99) * cc.rate;
    const a = (Number(pricing?.plans?.annual?.base_amount) || 83.88) * cc.rate;
    return {
      week1:  w,            // /week at 1 week
      week4:  m / 4.33,     // /week at 4 weeks (monthly equivalent)
      week52: a / 52,       // /week at 52 weeks (annual equivalent)
      weeklyBase: w,
      monthlyBase: m,
      annualBase: a,
    };
  }, [pricing, cc.rate]);

  // Smooth interpolation: 1→4 from week1→week4, 4→52 from week4→week52
  const perWeek = useMemo(() => {
    if (weeks <= 1) return anchors.week1;
    if (weeks >= 52) return anchors.week52;
    if (weeks <= 4) {
      const tFrac = (weeks - 1) / 3;
      return anchors.week1 + (anchors.week4 - anchors.week1) * tFrac;
    }
    const tFrac = (weeks - 4) / 48;
    return anchors.week4 + (anchors.week52 - anchors.week4) * tFrac;
  }, [weeks, anchors]);

  const totalCost = perWeek * weeks;
  const fullWeeklyCost = anchors.week1 * weeks;
  const savings = Math.max(0, fullWeeklyCost - totalCost);
  const savingsPct = fullWeeklyCost > 0 ? Math.round((savings / fullWeeklyCost) * 100) : 0;

  // Digit-roll: tween Per Week, Total Contract, Savings, and the strikethrough
  // weekly base whenever the slider OR currency changes. Same 350ms ease-out
  // cubic across all three for a consistent feel.
  const animPrice = useTweenedNumber(totalCost);
  const animPerWeek = useTweenedNumber(perWeek);
  const animSavings = useTweenedNumber(savings);
  const animStrike = useTweenedNumber(anchors.week1);

  // Tier label + colour band + one-line description. All defaults — admin
  // will be able to override label & desc via CMS once promoted to production.
  const tier =
    weeks <= 3   ? { id: 'weekly',  label: 'One Game at a Time', desc: 'For less than the price of a pie & Bovril.',          sub: 'Weekly tier',   accent: '#3b82f6' } :
    weeks <= 12  ? { id: 'monthly', label: 'Tournaments',         desc: 'Cup run on? Lock it in for a month.',                 sub: 'Monthly tier',  accent: '#0ea5e9' } :
    weeks <= 26  ? { id: 'half',    label: 'Underage Seasons',    desc: 'Built around school terms & U12–U16 cycles.',         sub: 'Mid-tier rate', accent: '#10b981' } :
                   { id: 'season',  label: 'Full Season',         desc: 'Pre-season to play-offs. The whole campaign.',        sub: 'Annual tier',   accent: '#f59e0b' };

  // Pretty contract-length string
  const lengthLabel = useMemo(() => {
    if (weeks === 1) return '1 week';
    if (weeks < 4) return `${weeks} weeks`;
    if (weeks === 4) return '1 month';
    if (weeks < 13) return `${weeks} weeks · ~${Math.round(weeks / 4.33)} months`;
    if (weeks === 13) return '1 quarter · ~3 months';
    if (weeks < 26) return `${weeks} weeks · ~${Math.round(weeks / 4.33)} months`;
    if (weeks === 26) return 'Half season · ~6 months';
    if (weeks < 52) return `${weeks} weeks · ~${Math.round(weeks / 4.33)} months`;
    return 'Full season · 1 year';
  }, [weeks]);

  const fmt = (n) => `${cc.symbol}${(Math.round(n * 100) / 100).toFixed(2)}`;

  // Testimonial-derived pricing (currency-aware)
  const testimonialPrice = TESTIMONIAL.priceEUR * cc.rate;
  const paybackMonthsRaw = anchors.annualBase > 0
    ? (testimonialPrice / anchors.annualBase) * 12
    : 0;
  const paybackMonths = Math.round(paybackMonthsRaw);
  const paybackYears = Math.round((paybackMonthsRaw / 12) * 10) / 10;
  // Show months when payback is under a year, years otherwise.
  const paybackDisplay = paybackMonthsRaw < 12
    ? `${paybackMonths} months`
    : `${paybackYears} yrs`;
  const fiveYearAnnualCost = anchors.annualBase * 5;
  const fiveYearSavings = Math.max(0, fiveYearAnnualCost - testimonialPrice);
  const foundersPct = Math.round((TESTIMONIAL.founders.claimed / TESTIMONIAL.founders.total) * 100);
  const foundersLeft = TESTIMONIAL.founders.total - TESTIMONIAL.founders.claimed;

  // Anchor markers along the rail
  const anchorMarks = [
    { w: 1,  label: 'Wk 1' },
    { w: 4,  label: '4 wk',  desktopOnly: true },
    { w: 13, label: '3 mo',  desktopOnly: true },
    { w: 26, label: '6 mo' },
    { w: 52, label: 'Season' },
  ];

  return (
    <div
      className={`min-h-screen ${t.bg} ${t.text} relative overflow-hidden`}
      style={{
        '--ball-thumb': `url(${ballFile})`,
        '--ball-rotation': `${ballRotation}deg`,
        '--fill-frac': `${(weeks - 1) / 51}`,
        // Tango ball is a full-bleed photo — strip the white tile + green ring
        // and let the image fill the thumb directly.
        '--thumb-bg': 'transparent',
        '--thumb-border': '0 solid transparent',
        '--thumb-size': '100%',
      }}
      data-testid="pricing-slider-mockup"
    >
      <style>{`
        @keyframes price-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
        .price-pop { animation: price-pop 0.35s ease-out; }

        @keyframes pulse-trophy {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.5), 0 10px 25px -5px rgba(251, 191, 36, 0.3); }
          50%      { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0), 0 14px 30px -5px rgba(251, 191, 36, 0.5); }
        }
        .animate-pulse-trophy { animation: pulse-trophy 2.2s ease-in-out infinite; }
      `}</style>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'}`}
        aria-label="Toggle theme" data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Logo */}
      <button onClick={() => navigate('/')} className="absolute top-6 left-6 z-20 flex items-center gap-2 group">
        <img src={DEFAULT_LOGO_URL} alt="Grassroots Coaching" className="w-10 h-10 object-contain group-hover:opacity-80 transition-opacity" />
        <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} hidden sm:inline`}>Grassroots Coaching</span>
      </button>

      <main className="relative min-h-screen pt-24 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#007a33]/10 border border-[#007a33]/30 mb-5">
              <span className="w-2 h-2 rounded-full bg-[#007a33] animate-pulse" />
              <span className="text-xs font-semibold tracking-widest uppercase text-[#007a33]">Concept · Mockup</span>
            </div>
            <h1 className={`text-4xl sm:text-6xl font-black tracking-tight mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Sign Your Contract.
            </h1>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Drag the ball. Pick how long you want to manage with us — the longer the deal, the lower the per-week rate.
            </p>

            {/* Currency toggle */}
            <div className="flex items-center justify-center gap-1 mt-6" data-testid="currency-toggle">
              {Object.entries(currencyConfig).map(([key, c]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCurrency(key)}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
                    currency === key
                      ? 'bg-[#007a33] text-white'
                      : isDark
                        ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  data-testid={`currency-${key}-btn`}
                >
                  {c.symbol} {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Option A label */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase bg-[#007a33] text-white">
              Option A
            </span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Founders&apos; Cap beyond the rail · {testimonialMode ? "Cap claimed" : 'tap the cap \u2192'}
            </span>
          </div>

          {/* Tier card — weekly (Option A primed state) */}
          {!testimonialMode && (
          <div
            className={`relative rounded-2xl border p-6 sm:p-8 mb-8 transition-colors ${
              isDark ? 'bg-gray-900/60 border-gray-800' : 'bg-white border-gray-200 shadow-xl shadow-gray-200/50'
            }`}
            data-testid="contract-card"
          >
            {/* Founders' Cap tile — top-right corner (absolute) */}
            <button
              type="button"
              onClick={() => setTestimonialMode(true)}
              className={`absolute top-4 right-4 sm:top-5 sm:right-5 z-10 flex flex-col items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-white shadow-lg shadow-amber-500/30 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/50 transition-all ${totalCost > testimonialPrice ? 'animate-pulse-trophy' : ''}`}
              data-testid="founders-cap-tile-btn"
              aria-label="Open Founders' Cap"
            >
              <FoundersCapIcon className="w-6 h-6 sm:w-7 sm:h-7 mb-0.5" />
              <span className="text-[6px] sm:text-[7px] font-bold tracking-widest uppercase opacity-80 leading-none">Earn a</span>
              <span className="text-[7px] sm:text-[9px] font-black tracking-widest uppercase leading-none mt-0.5">Founders&apos;</span>
              <span className="text-[7px] sm:text-[8px] font-black tracking-widest uppercase leading-none mt-0.5">Cap</span>
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[8px] font-black tracking-wider rounded-full bg-white text-amber-600 shadow">
                ∞
              </span>
            </button>

            {/* Tier badge + description (left-aligned, pr leaves room for cap tile) */}
            <div className="mb-6 pr-20 sm:pr-24">
              <span
                className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-[#007a33] text-white"
                data-testid="tier-badge"
              >
                {tier.label}
              </span>
              {tier.desc && (
                <p
                  className={`mt-2 text-xs sm:text-sm italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                  data-testid="tier-desc"
                >
                  {tier.desc}
                </p>
              )}
            </div>

            {/* Headline contract length + duration sub */}
            <div className="text-center mb-8">
              <div className={`text-7xl sm:text-8xl font-black leading-none mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="weeks-big">
                {weeks}
                <span className="text-3xl sm:text-4xl font-bold ml-2 align-top mt-3 inline-block opacity-70">{weeks === 1 ? 'week' : 'weeks'}</span>
              </div>
              <div className={`text-xs sm:text-sm flex items-center justify-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                <Calendar className="w-3.5 h-3.5" />
                <span data-testid="length-label">{lengthLabel}</span>
              </div>
            </div>

            {/* Slider — full width */}
            <div className="mb-2 px-1">
              <input
                ref={mockupSliderRef}
                type="range"
                min="1" max="52" step="1"
                value={weeks}
                onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
                className="contract-slider w-full"
                aria-label="Contract length in weeks"
                data-testid="contract-length-slider"
              />
            </div>

            {/* Anchor labels under rail */}
            <div className="relative h-6 mb-6 select-none">
              {anchorMarks.map(({ w, label, desktopOnly }) => {
                // Match the ball's true centre: thumb is 42px wide so its centre
                // travels from 21px (at min) to width - 21px (at max), not 0%→100%.
                const frac = (w - 1) / 51;
                const active = Math.abs(weeks - w) <= 0;
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setWeeks(w)}
                    className={`absolute -translate-x-1/2 text-[10px] sm:text-xs font-semibold transition-colors ${desktopOnly ? 'hidden md:block' : ''} ${
                      active ? 'text-[#007a33]' : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    style={{ left: `calc(16px + ${frac} * (100% - 32px))` }}
                    data-testid={`anchor-${w}`}
                  >
                    <span className={`block w-1 h-1 mx-auto mb-1 rounded-full ${active ? 'bg-[#007a33]' : isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Pricing grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {/* Per-week */}
              <div className={`p-4 rounded-xl ${isDark ? 'bg-black/30 border border-gray-800' : 'bg-gray-50 border border-gray-200'}`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Per week</div>
                <div className="flex items-baseline gap-2">
                  <div className={`text-2xl sm:text-3xl font-black price-pop ${isDark ? 'text-white' : 'text-gray-900'}`} key={`pw-${weeks}-${currency}`} data-testid="per-week-price">
                    {fmt(animPerWeek)}
                  </div>
                  {weeks > 1 && (
                    <div className={`text-sm line-through ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-testid="per-week-strike">
                      {fmt(animStrike)}
                    </div>
                  )}
                </div>
              </div>

              {/* Total contract */}
              <div
                className={`p-4 rounded-xl ${isDark ? 'bg-[#007a33]/15 border border-[#007a33]/40' : 'bg-[#007a33]/5 border border-[#007a33]/30'}`}
              >
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1 text-[#007a33]">Total contract</div>
                <div className={`text-2xl sm:text-3xl font-black price-pop ${isDark ? 'text-white' : 'text-gray-900'}`} key={`total-${weeks}-${currency}`} data-testid="total-price">
                  {fmt(animPrice)}
                </div>
              </div>

              {/* Savings */}
              <div className={`p-4 rounded-xl ${savings > 0
                ? 'bg-amber-500/10 border border-amber-500/40'
                : (isDark ? 'bg-black/30 border border-gray-800' : 'bg-gray-50 border border-gray-200')
              }`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${savings > 0 ? 'text-amber-500' : isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {savings > 0 ? <span className="inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" />You save</span> : 'No savings yet'}
                </div>
                <div className={`text-2xl sm:text-3xl font-black price-pop ${savings > 0 ? 'text-amber-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`} key={`save-${weeks}-${currency}`} data-testid="savings-amount">
                  {savings > 0 ? fmt(animSavings) : '—'}
                </div>
                {savings > 0 && (
                  <div className="text-xs font-semibold text-amber-500/80 mt-0.5" data-testid="savings-pct">
                    {savingsPct}% off the weekly rate
                  </div>
                )}
              </div>
            </div>

            {/* CTA */}
            <Button
              size="lg"
              className="w-full bg-[#007a33] hover:bg-[#005c26] text-white text-lg py-6 rounded-xl"
              data-testid="contract-sign-btn"
            >
              <Trophy className="w-5 h-5 mr-2" />
              Sign for {weeks === 1 ? '1 week' : `${weeks} weeks`} — {fmt(animPrice)}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <p className={`text-center text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Full premium access · cancel any time · no credit card required to start
            </p>
          </div>
          )}

          {/* Tier card — Testimonial (Option A active state) */}
          {testimonialMode && (
            <div
              className={`relative overflow-hidden rounded-2xl border-2 p-6 sm:p-8 mb-8 transition-colors ${
                isDark
                  ? 'bg-gradient-to-br from-amber-900/20 via-yellow-900/10 to-amber-900/20 border-amber-400/50'
                  : 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-amber-400/60 shadow-xl shadow-amber-200/40'
              }`}
              data-testid="testimonial-mode-card"
            >
              {/* Decorative gold glow */}
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />

              {/* Back link + Testimonial badge */}
              <div className="flex items-center justify-between mb-6 relative">
                <button
                  type="button"
                  onClick={() => setTestimonialMode(false)}
                  className={`text-xs font-semibold flex items-center gap-1.5 hover:underline ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                  data-testid="back-to-weekly-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
                <span
                  className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow flex items-center gap-1.5"
                  data-testid="founders-cap-badge"
                >
                  <FoundersCapIcon className="w-4 h-4" />
                  {TESTIMONIAL.name}
                </span>
              </div>

              {/* Headline */}
              <div className="text-center mb-6 relative">
                <FoundersCapIcon className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-2 drop-shadow-lg" />
                <div className={`text-4xl sm:text-5xl font-black mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  In from day one.
                </div>
                <p className={`text-sm sm:text-base max-w-md mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {TESTIMONIAL.tagline}
                </p>
              </div>

              {/* Price */}
              <div className="text-center mb-6 relative">
                <div
                  className={`text-6xl sm:text-7xl font-black leading-none mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}
                  data-testid="testimonial-price"
                >
                  {fmt(testimonialPrice)}
                </div>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  one-time payment · no renewals
                </div>
              </div>

              {/* Founders scarcity */}
              <div
                className={`rounded-xl border p-4 mb-6 relative ${isDark ? 'bg-black/40 border-amber-400/30' : 'bg-white/70 border-amber-300/50'}`}
                data-testid="founders-scarcity"
              >
                <div className="flex items-center justify-between text-xs font-semibold mb-2">
                  <span className="text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                    Founders&apos; Cap · First 100
                  </span>
                  <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-testid="founders-count">
                    {TESTIMONIAL.founders.claimed} of {TESTIMONIAL.founders.total} claimed · {foundersLeft} left
                  </span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all"
                    style={{ width: `${foundersPct}%` }}
                  />
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Only the first 100 coaches earn the Founders&apos; Cap. Locks today&apos;s price + every feature we ever ship. After 100, this offer disappears.
                </p>
              </div>

              {/* Value comparison */}
              <div className="grid grid-cols-2 gap-3 mb-6 relative">
                <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-black/30 border border-gray-800' : 'bg-white/80 border border-amber-200'}`}>
                  <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>vs annual</div>
                  <div className={`text-base sm:text-lg font-black leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Pays for itself<br />in ~{paybackDisplay}
                  </div>
                </div>
                <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-black/30 border border-gray-800' : 'bg-white/80 border border-amber-200'}`}>
                  <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>After 5 yrs</div>
                  <div className="text-base sm:text-lg font-black leading-tight text-amber-600 dark:text-amber-400">
                    {fmt(fiveYearSavings)} saved
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Button
                size="lg"
                className="relative w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white text-lg py-6 rounded-xl shadow-lg shadow-amber-500/30"
                data-testid="founders-cap-cta-btn"
              >
                <FoundersCapIcon className="w-6 h-6 mr-2" />
                Claim my Founders&apos; Cap — {fmt(testimonialPrice)}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <p className={`text-center text-xs mt-3 relative ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                One-time payment · A new Cap every contract anniversary · Price-locked forever
              </p>
            </div>
          )}

          {/* ====================================================
              LOYALTY LADDER — animation prototype
              Football kicks off from Year 1 and rolls to Year 10.
              Each waypoint reveals its discount + emits a starburst.
              Year 10 gets a stronger peak burst. Triggered ONCE per
              page load when the section enters the viewport.
              Respects prefers-reduced-motion.
              CMS toggles + promotion to LoyaltyLadderSection come next.
             ==================================================== */}
          <LoyaltyLadderAnimated isDark={isDark} />

          {/* Live-math explainer */}
          <div className={`text-center text-xs ${isDark ? 'text-gray-600' : 'text-gray-500'} max-w-xl mx-auto leading-relaxed`}>
            <p>
              Per-week rate slides smoothly from <strong className={isDark ? 'text-gray-400' : 'text-gray-700'}>{fmt(anchors.week1)}</strong> at 1 week,
              through <strong className={isDark ? 'text-gray-400' : 'text-gray-700'}>{fmt(anchors.week4)}</strong> at 1 month,
              down to <strong className={isDark ? 'text-gray-400' : 'text-gray-700'}>{fmt(anchors.week52)}</strong> at a full season.
              Every extra week tilts the rate closer to the next tier.
            </p>
          </div>

          {/* ====================================================
              OPTION C — Standalone Testimonial card below slider
              Always visible. Compact horizontal layout so the
              page presents A and C side-by-side for comparison.
             ==================================================== */}
          <div className="mt-16 mb-3 flex items-center justify-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase bg-amber-500 text-white">
              Option C
            </span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Standalone card · always visible below slider
            </span>
          </div>

          <div
            className={`relative overflow-hidden rounded-2xl border-2 p-6 sm:p-7 transition-colors ${
              isDark
                ? 'bg-gradient-to-br from-amber-900/15 to-yellow-900/10 border-amber-400/40'
                : 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 shadow-lg shadow-amber-100/40'
            }`}
            data-testid="testimonial-standalone-card"
          >
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />

            {/* Compact horizontal layout: left = pitch, right = CTA */}
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-center relative">
              {/* LEFT — Founders' Cap icon */}
              <div className="hidden md:flex items-center justify-center">
                <FoundersCapIcon className="w-20 h-20 drop-shadow-lg" />
              </div>

              {/* MIDDLE — pitch */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow flex items-center gap-1">
                    <FoundersCapIcon className="w-3.5 h-3.5" /> {TESTIMONIAL.name}
                  </span>
                  <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                    {foundersLeft} of {TESTIMONIAL.founders.total} caps left
                  </span>
                </div>

                <h3 className={`text-2xl sm:text-3xl font-black mb-1 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  In from day one. Stays with you forever.
                </h3>
                <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Your Founders&apos; Cap locks today&apos;s price + earns a new Cap every anniversary. Pays for itself in <strong className={isDark ? 'text-amber-400' : 'text-amber-600'}>~{paybackDisplay}</strong> vs annual.
                </p>

                {/* Mini scarcity bar */}
                <div className={`h-1.5 rounded-full overflow-hidden mb-2 ${isDark ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-yellow-500"
                    style={{ width: `${foundersPct}%` }}
                  />
                </div>
                <p className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Includes every feature we ever launch · a new Cap awarded each year
                </p>
              </div>

              {/* RIGHT — price + CTA */}
              <div className="md:text-right md:border-l md:pl-6 md:border-amber-300/40">
                <div className={`text-4xl sm:text-5xl font-black leading-none mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="founders-cap-standalone-price">
                  {fmt(testimonialPrice)}
                </div>
                <div className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  one-time · no renewals
                </div>
                <Button
                  className="w-full md:w-auto bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white px-6 py-5 rounded-xl shadow-lg shadow-amber-500/30 font-bold"
                  data-testid="founders-cap-standalone-cta-btn"
                >
                  <FoundersCapIcon className="w-5 h-5 mr-2" />
                  Claim my Founders&apos; Cap
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ============================================================
   LoyaltyLadderAnimated
   ============================================================
   Prototype of the "football rolls up the ladder" reveal.

   Behaviour:
   - Sits idle at initial state until IntersectionObserver fires
   - Adds `.ll-animating` class → CSS-driven ball roll, dot glows,
     starbursts and discount reveals run in parallel via delays
   - Plays ONCE per page load by default
   - "Replay" button (dev-only, in the top-right of the card) lets
     us tune the timing without a full page refresh
============================================================ */
const LOYALTY_BALL_URL = 'https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/jyfl98do_Untitled%20design%20-%202026-06-12T185108.908.png';

const LADDER_MILESTONES = [
  { yr: 'Today',   pct: 'Save', peak: false, isToday: true },
  { yr: 'Year 1',  pct: '5%',   peak: false },
  { yr: 'Year 3',  pct: '15%',  peak: false },
  { yr: 'Year 5',  pct: '25%',  peak: false },
  { yr: 'Year 7',  pct: '35%',  peak: false },
  { yr: 'Year 10', pct: '50%',  peak: true  },
];

// Timing: total 3.6s. Milestone arrival = (idx / (count-1)) * 3.6s.
// The reveal takes 0.28s and the starburst 0.55s, so we fire them slightly
// BEFORE the ball actually lands (`REVEAL_LEAD_S`) so both peak just as the
// ball hits the dot — otherwise the text pops in AFTER the ball has moved on.
const TOTAL_DURATION_S = 3.6;
const REVEAL_LEAD_S = 0.14;
const stepDelayS = (idx, count) => {
  if (count <= 1) return 0;
  const pct = idx / (count - 1);
  return Math.max(0, pct * TOTAL_DURATION_S - REVEAL_LEAD_S);
};

function LoyaltyLadderAnimated({ isDark }) {
  const containerRef = useRef(null);
  const railRef = useRef(null);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'animating' | 'done'
  const [runKey, setRunKey] = useState(0);

  // IntersectionObserver → play once when scrolled into view.
  // Wait 500ms after the ladder enters view so the visitor has time
  // to register the section before the ball kicks off (no surprise motion).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (phase !== 'idle') return;
    let startTimer = null;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio > 0.4) {
          io.disconnect();
          startTimer = setTimeout(() => setPhase('animating'), 500);
          break;
        }
      }
    }, { threshold: [0, 0.4, 0.6] });
    io.observe(el);
    return () => {
      io.disconnect();
      if (startTimer) clearTimeout(startTimer);
    };
  }, [phase, runKey]);

  // Timer to flip animating → done once the sequence finishes.
  useEffect(() => {
    if (phase !== 'animating') return;
    const t = setTimeout(() => setPhase('done'), TOTAL_DURATION_S * 1000 + 100);
    return () => clearTimeout(t);
  }, [phase, runKey]);

  const replay = () => {
    setPhase('idle');
    setRunKey(k => k + 1);
    // Next tick force animating
    requestAnimationFrame(() => setPhase('animating'));
  };

  const stateClass =
    phase === 'animating' ? 'll-animating'
    : phase === 'done'    ? 'll-static'
    : '';

  return (
    <div
      ref={containerRef}
      key={runKey}
      className={`mt-8 rounded-2xl border p-5 sm:p-6 relative overflow-hidden ll-container ${stateClass} ${
        isDark
          ? 'bg-gradient-to-br from-amber-900/15 to-yellow-900/5 border-amber-400/30'
          : 'bg-gradient-to-br from-amber-50 to-yellow-50/60 border-amber-300/60 shadow-md shadow-amber-100/40'
      }`}
      data-testid="loyalty-ladder-animated"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />

      {/* Header row — pill on left, tagline + Replay button on right */}
      <div className="flex items-center justify-between mb-4 relative gap-3">
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-sm shrink-0">
          Loyalty Ladder
        </span>
        <div className="flex items-center gap-3 min-w-0 justify-end">
          <span className={`text-xs sm:text-sm font-semibold text-right truncate ${isDark ? 'text-amber-300/90' : 'text-amber-700'}`}>
            The longer you coach with us, the cheaper we get.
          </span>
          <button
            type="button"
            onClick={replay}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-amber-500/40 text-amber-700 hover:bg-amber-500/10 transition-colors shrink-0"
            data-testid="loyalty-ladder-replay-btn"
          >
            Replay
          </button>
        </div>
      </div>

      {/* Milestone rail */}
      <div ref={railRef} className="relative pt-2 pb-1 mb-2" style={{ minHeight: 62 }}>
        {/* Base rail line — spans exactly from Today dot centre to Year 10 dot centre */}
        <div
          className={`absolute top-[18px] h-0.5 ${isDark ? 'bg-amber-400/20' : 'bg-amber-300/50'}`}
          style={{ left: '8.333%', right: '8.333%' }}
        />
        {/* Filling rail — width driven by CSS animation */}
        <div
          className="ll-rail-fill absolute top-[18px] h-0.5 bg-gradient-to-r from-amber-400 to-yellow-500"
          data-testid="ll-rail-fill"
        />
        {/* Rolling football */}
        <div
          className="ll-ball"
          style={{ backgroundImage: `url(${LOYALTY_BALL_URL})` }}
          aria-hidden="true"
          data-testid="ll-ball"
        />

        <div className="relative grid grid-cols-6 gap-1">
          {LADDER_MILESTONES.map((m, idx) => {
            const delay = stepDelayS(idx, LADDER_MILESTONES.length);
            const isToday = m.isToday;
            return (
              <div
                key={m.yr}
                className="flex flex-col items-center text-center"
                data-testid={`ll-step-${idx}`}
              >
                <div className="ll-dot-wrap mb-1.5">
                  <div
                    className={`ll-dot ${m.peak ? 'll-dot--peak' : ''} ${isToday ? 'll-dot--today' : ''}`}
                    style={isToday ? undefined : { animationDelay: `${delay}s` }}
                  />
                  {!isToday && (
                    <div
                      className={`ll-starburst ${m.peak ? 'll-starburst--peak' : ''}`}
                      style={{ animationDelay: `${delay}s` }}
                    />
                  )}
                </div>
                <span className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider leading-none ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {m.yr}
                </span>
                <span
                  className={`ll-reveal text-base sm:text-lg font-black leading-tight mt-0.5 ${
                    m.peak
                      ? 'text-amber-500'
                      : isToday
                        ? (isDark ? 'text-gray-600' : 'text-gray-400')
                        : isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}
                  style={{ animationDelay: isToday ? '0s' : `${delay}s` }}
                >
                  {m.pct}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer pitch */}
      <p className={`text-xs sm:text-sm mt-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        Most apps charge loyal users more.
        <strong className={`ml-1 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>We do the opposite.</strong>
        {' '}Your renewal price drops every contract anniversary &mdash; for life.
      </p>
    </div>
  );
}

